import { ArrowRightOutlined, LoadingOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import React, { useEffect, useRef, useState } from 'react';
import { ChatSendIcon } from '../../../assets/CustomIcons/ChatSendIcon';
import { BaseButton } from '../../UI/BaseButton';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  contextText?: string;
}

interface AIChatInterfaceProps {
  messages: ChatMessage[];
  isLoading: boolean;
  userInput: string;
  onUserInputChange: (value: string) => void;
  onSendMessage: () => void;
  disabled?: boolean;
  placeholder?: string;
  loadingMessage?: string;
  defaultPrompt?: {
    text: string;
    buttonLabel: string;
  };
  showDefaultPrompt?: boolean;
  contextBanner?: React.ReactNode;
  suggestedActions?: React.ReactNode;
  onSendPrompt?: (prompt: string) => void;
  suggestedActionsPlacement?: 'afterMessages' | 'afterContext';
  focusTriggerRef?: React.MutableRefObject<(() => void) | undefined>;
}

export const AIChatInterface: React.FC<AIChatInterfaceProps> = ({
  messages,
  isLoading,
  userInput,
  onUserInputChange,
  onSendMessage,
  disabled = false,
  placeholder = 'Type your message...',
  loadingMessage = 'Generating...',
  defaultPrompt,
  showDefaultPrompt = false,
  contextBanner,
  suggestedActions,
  onSendPrompt,
  suggestedActionsPlacement = 'afterContext',
  focusTriggerRef,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<any>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Expose focus function through ref
  useEffect(() => {
    if (focusTriggerRef) {
      focusTriggerRef.current = () => {
        inputRef.current?.focus();
      };
    }
  }, [focusTriggerRef]);

  return (
    <div className="flex flex-col h-full">
      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto px-6 pb-4">
        {messages.map((message, index) => {
          // Check if this is the last assistant message being streamed
          const isLastMessage = index === messages.length - 1;
          const isStreamingNow = isLoading && isLastMessage && message.role === 'assistant';
          const hasContent = message.content && message.content.trim() !== '';

          return (
            <div key={message.id} className="mb-4">
              {/* Context text (reference) - shown above the message bubble */}
              {message.contextText && message.role === 'user' && (
                <div className="ml-4 mb-1">
                  <div className="flex items-center gap-2 p-3 bg-neutral-200 border border-neutral-300 rounded-lg">
                    <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-neutral-900">
                      <ArrowRightOutlined />
                    </div>
                    <div className="flex-1 text-sm leading-4 text-neutral-900 overflow-hidden line-clamp-2 break-words">
                      {message.contextText}
                    </div>
                  </div>
                </div>
              )}

              {/* Message bubble */}
              <div
                className={`${
                  message.role === 'user'
                    ? 'bg-neutral-100 ml-4 px-4 py-0.5 rounded-lg text-brand-grey-dark font-normal'
                    : 'text-brand-grey-dark'
                }`}
              >
                {hasContent ? (
                  <div
                    className={`text-sm leading-6 ${message.role === 'user' ? 'font-medium' : ''}`}
                    dangerouslySetInnerHTML={{ __html: message.content }}
                  />
                ) : (
                  // Show placeholder for empty streaming message
                  <div className="text-sm leading-6 h-6" />
                )}
              </div>
            </div>
          );
        })}

        {/* Default Prompt - Show only for first greeting message */}
        {showDefaultPrompt && messages.length === 1 && onSendPrompt && defaultPrompt && !isLoading && (
          <div className="my-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex-1 h-px bg-neutral-300" />
              <span className="text-xs text-neutral-800">or</span>
              <div className="flex-1 h-px bg-neutral-300" />
            </div>
            <BaseButton
              type="secondary"
              size="large"
              onClick={() => onSendPrompt(defaultPrompt.text)}
              disabled={disabled}
            >
              {defaultPrompt.buttonLabel}
            </BaseButton>
          </div>
        )}

        {isLoading && (
          <div className="mb-4 text-neutral-800">
            <div className="flex items-center gap-2 text-sm">
              <LoadingOutlined /> <span>{loadingMessage}</span>
            </div>
          </div>
        )}

        {/* Suggested Actions - After Messages */}
        {suggestedActionsPlacement === 'afterMessages' && suggestedActions}

        <div ref={messagesEndRef} />
      </div>

      {/* Context Banner */}
      {contextBanner}

      {/* Suggested Actions - After Context */}
      {suggestedActionsPlacement === 'afterContext' && suggestedActions}

      {/* Chat Input */}
      <div className="p-2">
        <div
          className={`
            box-border flex items-center gap-2 border border-solid rounded-lg px-2 py-1 transition-colors bg-white
            ${isInputFocused ? 'border-taskonioGreen-500 outline-taskonioGreen-300 outline-1' : 'border-neutral-300'}
          `}
        >
          {/* Textarea */}
          <TextArea
            ref={inputRef}
            className="flex-1 text-sm text-neutral-900 placeholder-neutral-700 !border-none !outline-none resize-none bg-transparent !shadow-none"
            placeholder={placeholder}
            value={userInput}
            onChange={(e) => onUserInputChange(e.target.value)}
            onFocus={() => setIsInputFocused(true)}
            onBlur={(e) => {
              // Don't blur if clicking inside the input container
              if (!e.currentTarget.parentElement?.contains(e.relatedTarget as Node)) {
                setIsInputFocused(false);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !disabled && userInput.trim()) {
                e.preventDefault();
                onSendMessage();
              }
            }}
            disabled={disabled || isLoading}
            autoSize={{ minRows: 1, maxRows: 6 }}
            styles={{
              textarea: {
                padding: '0 !important',
              },
            }}
            variant="borderless"
          />

          {/* Send Button */}
          <div
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!disabled && !isLoading && userInput.trim()) {
                onSendMessage();
              }
            }}
          >
            <BaseButton type="accent" size="small" disabled={disabled || isLoading || !userInput.trim()}>
              <ChatSendIcon className="text-white" />
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  );
};
